import React from 'react';
import { Asset as AtomicAsset } from '../../../AtomicComponents/Asset';
import { Asset } from 'contentful';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Block, Inline } from '@contentful/rich-text-types';
import { format } from 'date-fns';

import { ILearningCenterArticleFields } from '@/types/contentful';
import { ImageType } from './types';

import RichText from '../../../RichText';
import { ArticleLink } from './ArticleLink';

import cx from 'classnames';
import { categoryColor } from '../shared';

import { useRouter } from 'next/router';

interface ArticleSnippetProps {
  fields?: ILearningCenterArticleFields;
  showSummary?: boolean;
  image?: ImageType;
  listMode?: boolean;
}

const overrideOptions: RenderNode = {
  [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
    <p className="typography-body-2 line-clamp-1 overflow-hidden [text-overflow:ellipsis]">
      {children}
    </p>
  )
};

interface ArrowIconProps {
  className?: string;
  stroke?: string;
}

export const ArrowIcon: React.FC<ArrowIconProps> = ({
  className,
  stroke = '#1D252C'
}) => (
  <svg
    width="11"
    height="10"
    viewBox="0 0 11 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M1 5H10M10 5L6 1M10 5L6 9"
      stroke={stroke}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function ArticleSnippet({
  fields,
  showSummary = false,
  image = 'none',
  listMode = false
}: ArticleSnippetProps) {
  const router = useRouter();
  const goToLCPage = router.asPath.includes('learning-center');

  const handleCategoryClick = (url: string, event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    router.push(url);
  };

  const publishDate =
    fields?.publishDate && format(new Date(fields?.publishDate), 'MM.d.yy');

  return (
    <ArticleLink
      category={fields?.category}
      slug={fields?.slug}
      className={cx('group flex w-full flex-col', {
        'w-[385px]': image === 'none' && listMode == false
      })}
      goToLCPage={goToLCPage}
    >
      <div
        className={cx(
          'box-border flex flex-col overflow-hidden rounded-2xl bg-white p-4 transition-colors duration-200 ease-in-out',
          {
            'h-full': image,
            'h-auto': image === 'none',
            'desktop:pb-4': image !== 'none' && !listMode
          }
        )}
        data-testid="article-snippet"
      >
        {image !== 'none' && fields?.heroImage && (
          <div
            className="!relative flex max-h-[272px] w-full flex-shrink-0 items-center justify-center overflow-hidden rounded-card-shape"
            data-testid="gl-img"
          >
            <AtomicAsset
              asset={fields?.heroImage as Asset}
              className="block w-full max-w-full rounded-card-shape object-cover"
            />
          </div>
        )}
        <div className="flex w-full flex-grow flex-col justify-between">
          <div className="flex flex-grow flex-col">
            <button
              onClick={(event) =>
                fields?.category
                  ? handleCategoryClick(
                      goToLCPage
                        ? `/learning-center/${fields.category.fields.slug}`
                        : `/partner-resources/${fields.category.fields.slug}`,
                      event
                    )
                  : null
              }
              className={cx(
                `group/button relative flex w-fit items-center rounded-full px-[6px] py-[2px] text-xs font-medium leading-4`,
                image !== 'none' ? 'my-2' : 'mb-2'
              )}
              style={{
                background:
                  categoryColor[fields?.category?.fields?.slug || 'popular']
              }}
            >
              {fields?.category?.fields?.name || 'Popular'}
              <ArrowIcon className="ml-1 hidden transition-all duration-300 group-hover/button:block" />
            </button>
            <h4
              className={cx(
                'article-headline typography-title-4 line-clamp-2 flex-grow self-stretch font-medium leading-6 text-[#1D252C] hover:underline active:underline',
                {
                  'mb-3': image === 'none'
                }
              )}
              data-testid="learningcenter-article-headline"
            >
              {fields?.title}
            </h4>
            {showSummary && fields?.summary && (
              <RichText
                data={fields?.summary}
                overrideOptions={overrideOptions}
              />
            )}
            <div
              className={cx(
                'flex items-center gap-[0_4px] text-[12px] font-medium leading-[16px]',
                {
                  'mb-0': image === 'none'
                }
              )}
            >
              {fields?.tagsList ? (
                <>
                  <p className="leading-5">
                    {fields.tagsList.map((tag) => tag.fields.name).join(' ')}
                  </p>
                  <p className="text-[10px]leading-4">•</p>
                </>
              ) : null}
              <p className="font-medium leading-5">{fields?.difficulty}</p>
              <p className="text-[10px]leading-4">•</p>
              {publishDate && (
                <p className="font-normal leading-5">{publishDate}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </ArticleLink>
  );
}
